import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Segmented, Table, Tag, Button, Modal, Form, Input, Select, TimePicker,
  Switch, Checkbox, Popconfirm, message, Empty, List, Space, Popover, Alert, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, ClockCircleOutlined,
  CheckCircleTwoTone, CloseCircleOutlined, LockOutlined,
  CalendarOutlined, FireOutlined, FormOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getRoutines, createRoutine, updateRoutine, toggleRoutineActive, deleteRoutine,
  getRoutineExecutions, completeRoutineExecution, overrideRoutineExecution,
  getRoutineAssignableUsers,
} from '../api/routines';
import { getBranches } from '../api/branches';
import UserSearchSelect from '../components/common/UserSearchSelect';
import BranchSelect from '../components/common/BranchSelect';
import useAuthStore from '../store/authStore';
import useServerTable from '../hooks/useServerTable';
import { apiErrorMessage } from '../utils/apiError';
import { ROUTINE_COLOR_OPTIONS, routineColorHex } from '../constants/routineColors';
import {
  ROUTINE_WEEKDAY_OPTIONS, scheduleSummary,
  ROUTINE_FREQUENCY_OPTIONS, ROUTINE_MONTHLY_MODE_OPTIONS, ROUTINE_MONTH_WEEK_OPTIONS, MONTH_DAY_OPTIONS,
} from '../constants/routineWeekdays';

const { TextArea } = Input;
const MANAGER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];
// Sube rutini SILME sadece bu rollerde (backend de ayni kurali uyguluyor).
// Sube muduru rutini aktif/pasif ile yonetiyor - gecmis icra kayitlari
// silinen bir sablona bagli kalmasin diye.
const TOP_ROLES = ['developer_admin', 'super_admin'];

// Rutin tanimindaki uc ac/kapa kurali - listede tek sutunda ozetlenip
// uzerine gelinince popover ile aciliyor.
const RULE_FIELDS = [
  {
    key: 'skip_weekday_if_holiday',
    icon: <CalendarOutlined />,
    short: 'Tatilde Atla',
    label: 'Seçili gün resmi tatile denk gelirse atla',
  },
  {
    key: 'also_fire_on_any_holiday',
    icon: <FireOutlined />,
    short: 'Tatilde de Çalış',
    label: 'Haftanın gününden bağımsız, her resmi tatilde de çalıştır',
  },
  {
    key: 'require_completion_note',
    icon: <FormOutlined />,
    short: 'Not Zorunlu',
    label: 'Tamamlarken not girmek zorunlu',
  },
];

// Uc kural, sutunu sismeden gostersin diye metin yerine kucuk ikon olarak
// ciziliyor (uzun etiketler tabloyu tasirip Islemler sutununu ekran disina
// itiyordu). Detay uzerine gelince popover ile aciliyor.
const RulesCell = ({ routine }) => (
  <Popover
    title="Rutin Kuralları"
    content={
      <div style={{ maxWidth: 320 }}>
        {RULE_FIELDS.map((f) => (
          <div key={f.key} className="flex items-start gap-2" style={{ padding: '3px 0' }}>
            {routine[f.key]
              ? <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginTop: 4 }} />
              : <CloseCircleOutlined style={{ color: '#bfbfbf', marginTop: 4 }} />}
            <span style={{ color: routine[f.key] ? 'inherit' : '#8c8c8c' }}>
              <strong>{f.short}</strong> — {f.label}
            </span>
          </div>
        ))}
      </div>
    }
  >
    <Space size={10} style={{ cursor: 'help' }}>
      {RULE_FIELDS.map((f) => (
        <span
          key={f.key}
          aria-label={`${f.short}: ${routine[f.key] ? 'açık' : 'kapalı'}`}
          style={{
            fontSize: 16,
            lineHeight: 1,
            color: routine[f.key] ? '#1677ff' : '#d9d9d9',
          }}
        >
          {f.icon}
        </span>
      ))}
    </Space>
  </Popover>
);

// Kalabalik atama listesi satiri tasirip Islemler sutununu ekran disina
// itiyordu - ilk iki isim gosterilip gerisi "+N" olarak toplaniyor, tam liste
// uzerine gelince aciliyor.
const AssigneesCell = ({ assignments }) => {
  const names = (assignments || []).map((a) => `${a.user?.name} ${a.user?.surname}`);
  if (names.length === 0) return '—';

  const shown = names.slice(0, 2);
  const rest = names.length - shown.length;

  return (
    <Tooltip title={names.join(', ')}>
      <span style={{ cursor: names.length > 2 ? 'help' : 'default' }}>
        {shown.join(', ')}
        {rest > 0 && <Tag style={{ marginLeft: 6 }}>+{rest}</Tag>}
      </span>
    </Tooltip>
  );
};

// DIKKAT: `createdBy` iliskisi JSON'a `created_by` olarak seri hale geliyor ve
// ayni adli FK kolonunu EZIYOR - yani alan bazen tam sayi, iliski yuklendiginde
// ise {id,name,surname} objesi oluyor. Iki durumu da tolere ediyoruz.
const creatorId = (routine) =>
  (routine?.created_by && typeof routine.created_by === 'object')
    ? routine.created_by.id
    : routine?.created_by;

const creatorName = (routine) =>
  (routine?.created_by && typeof routine.created_by === 'object')
    ? `${routine.created_by.name} ${routine.created_by.surname}`
    : null;

// ------------------------------------------------------------------
// Bugün: sube + kisisel rutinlerin bugunku icralari, tek listede
// ------------------------------------------------------------------
const TodayTab = () => {
  const { user } = useAuthStore();
  const isManager = MANAGER_ROLES.includes(user?.role);
  // Sube icrasinin saatini yalnizca yoneticiler degistirebiliyor (backend de
  // ayni kurali uyguluyor); kisisel rutinde sahibi kendi saatini degistirir.
  const canEditTime = (item) =>
    item.routine?.type === 'branch' ? isManager : item.owner_user_id === user?.id;

  const [timeItem, setTimeItem] = useState(null);
  const [newTime, setNewTime] = useState(null);
  const [savingTime, setSavingTime] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completeItem, setCompleteItem] = useState(null);
  const [completeNote, setCompleteNote] = useState('');
  const [completing, setCompleting] = useState(false);

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const [branchRes, personalRes] = await Promise.all([
        getRoutineExecutions({ type: 'branch', per_page: 100 }),
        getRoutineExecutions({ type: 'personal', per_page: 100 }),
      ]);
      const merged = [...branchRes.data.data, ...personalRes.data.data]
        .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
      setItems(merged);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Bugünün rutinleri yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const openComplete = (item) => {
    setCompleteItem(item);
    setCompleteNote('');
  };

  const handleComplete = async () => {
    if (completeItem?.require_completion_note && !completeNote.trim()) {
      message.warning('Bu rutin için not girmek zorunlu.');
      return;
    }
    setCompleting(true);
    try {
      await completeRoutineExecution(completeItem.id, { note: completeNote.trim() || undefined });
      message.success('Rutin tamamlandı.');
      setCompleteItem(null);
      fetchToday();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Tamamlanamadı.'));
    } finally {
      setCompleting(false);
    }
  };

  const openTimeEdit = (item) => {
    setTimeItem(item);
    setNewTime(dayjs(item.scheduled_time, 'HH:mm:ss'));
  };

  const handleSaveTime = async () => {
    if (!newTime) {
      message.warning('Saat seçin.');
      return;
    }
    setSavingTime(true);
    try {
      // Yalnizca SAAT gonderiliyor - override ucu sadece bugunku icrayi
      // degistirir, rutin sablonu (ve yarinki icra) etkilenmez.
      await overrideRoutineExecution(timeItem.id, { scheduled_time: newTime.format('HH:mm') });
      message.success('Bugünkü saat güncellendi.');
      setTimeItem(null);
      fetchToday();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Saat güncellenemedi.'));
    } finally {
      setSavingTime(false);
    }
  };

  const statusOf = (item) => {
    if (item.completed_at) return 'completed';
    const scheduled = dayjs(`${item.date} ${item.scheduled_time}`);
    return dayjs().isAfter(scheduled) ? 'overdue' : 'pending';
  };

  return (
    <Card>
      <List
        loading={loading}
        dataSource={items}
        locale={{ emptyText: <Empty description="Bugün için rutin yok." /> }}
        renderItem={(item) => {
          const status = statusOf(item);
          // Aciklama snapshot alani sonradan eklendi - eski icra kayitlarinda
          // bos oldugu icin sablondakine geri dusuyoruz.
          const description = item.description || item.routine?.description;
          return (
            <List.Item
              actions={[
                status !== 'completed' && canEditTime(item) && (
                  <Tooltip key="time" title="Yalnızca bugünkü saati değiştirir — rutinin kendi saati değişmez.">
                    <Button size="small" icon={<ClockCircleOutlined />} onClick={() => openTimeEdit(item)}>
                      Saati Düzenle
                    </Button>
                  </Tooltip>
                ),
                status !== 'completed' && (
                  <Button key="complete" size="small" type="primary" icon={<CheckOutlined />} onClick={() => openComplete(item)}>
                    Tamamla
                  </Button>
                ),
              ].filter(Boolean)}
            >
              <List.Item.Meta
                title={
                  <div className="flex items-center gap-2 flex-wrap">
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: routineColorHex(item.color) }} />
                    <span>{item.name}</span>
                    <Tag>{item.scheduled_time?.slice(0, 5)}</Tag>
                    {item.is_same_day_override && (
                      <Tooltip title="Bugüne özel saat değişikliği yapıldı.">
                        <Tag color="gold">bugüne özel</Tag>
                      </Tooltip>
                    )}
                    <Tag color={item.routine?.type === 'branch' ? 'blue' : 'default'}>
                      {item.routine?.type === 'branch' ? 'Şube' : 'Kişisel'}
                    </Tag>
                    {status === 'completed' && <Tag color="green">Tamamlandı</Tag>}
                    {status === 'overdue' && <Tag color="red">Gecikti</Tag>}
                    {status === 'pending' && <Tag color="orange">Bekliyor</Tag>}
                    {item.require_completion_note && <Tag color="purple">Not zorunlu</Tag>}
                  </div>
                }
                description={
                  <div className="text-sm">
                    {description && (
                      <div style={{ whiteSpace: 'pre-wrap', color: '#434343', marginBottom: 4 }}>
                        {description}
                      </div>
                    )}
                    <div className="text-gray-500">
                      {item.assignments?.length > 0 && (
                        <span>Atanan: {item.assignments.map((a) => `${a.user?.name} ${a.user?.surname}`).join(', ')} </span>
                      )}
                      {item.completed_at && (
                        <span>— {item.completed_by?.name} {item.completed_by?.surname} tarafından {dayjs(item.completed_at).format('HH:mm')}'de tamamlandı{item.completion_note ? `: ${item.completion_note}` : ''}</span>
                      )}
                    </div>
                  </div>
                }
              />
            </List.Item>
          );
        }}
      />

      <Modal
        title="Rutini Tamamla"
        open={!!completeItem}
        onCancel={() => setCompleteItem(null)}
        onOk={handleComplete}
        confirmLoading={completing}
        okText="Tamamla"
        cancelText="Vazgeç"
      >
        {(completeItem?.description || completeItem?.routine?.description) && (
          <div
            className="mb-3 p-2 rounded text-sm text-gray-600"
            style={{ whiteSpace: 'pre-wrap', background: '#fafafa' }}
          >
            {completeItem.description || completeItem.routine?.description}
          </div>
        )}
        <p className="text-gray-500 mb-2">
          {completeItem?.require_completion_note ? 'Bu rutin için not zorunludur.' : 'İsterseniz bir not ekleyin (opsiyonel).'}
        </p>
        <TextArea
          value={completeNote}
          onChange={(e) => setCompleteNote(e.target.value)}
          placeholder="Not..."
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={2000}
        />
      </Modal>

      <Modal
        title="Bugünkü Saati Düzenle"
        open={!!timeItem}
        onCancel={() => setTimeItem(null)}
        onOk={handleSaveTime}
        confirmLoading={savingTime}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <p className="text-gray-500 mb-3">
          <strong>{timeItem?.name}</strong> rutininin <strong>yalnızca bugünkü</strong> saati
          değişir. Rutin tanımı ve yarınki tekrarı etkilenmez.
        </p>
        <TimePicker
          value={newTime}
          onChange={setNewTime}
          format="HH:mm"
          style={{ width: '100%' }}
          allowClear={false}
        />
      </Modal>
    </Card>
  );
};

// ------------------------------------------------------------------
// Ortak rutin tanimi formu (sube + kisisel formlarinda paylasilan alanlar)
// ------------------------------------------------------------------
const RoutineFormFields = ({ isBranch, branchId, initialAssignedUsers }) => (
  <>
    <Form.Item name="name" label="Rutin Adı" rules={[{ required: true, message: 'Rutin adı zorunlu.' }]}>
      <Input placeholder="Örn. Klimayı Aç" maxLength={150} />
    </Form.Item>
    <Form.Item
      name="description"
      label="Açıklama"
      extra="Rutini yapacak kişi bu açıklamayı günlük listede görecek — ne yapılması gerektiğini yazın."
    >
      <TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={2000} />
    </Form.Item>
    <div className="flex gap-3">
      <Form.Item name="scheduled_time" label="Saat" rules={[{ required: true, message: 'Saat zorunlu.' }]} style={{ flex: 1 }}>
        <TimePicker format="HH:mm" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="color" label="Renk" style={{ flex: 1 }}>
        <Select options={ROUTINE_COLOR_OPTIONS.map((o) => ({ value: o.value, label: (<span><span style={{ color: o.hex }}>●</span> {o.label}</span>) }))} />
      </Form.Item>
    </div>
    <Form.Item name="frequency" label="Tekrar">
      <Select options={ROUTINE_FREQUENCY_OPTIONS} />
    </Form.Item>
    {/*
      Tekrar kuralina gore alan degistiriyoruz. shouldUpdate render-prop'u
      bilerek: form ornegini bu bilesene prop olarak gecirmeye gerek kalmiyor.
    */}
    <Form.Item
      noStyle
      shouldUpdate={(prev, cur) => prev.frequency !== cur.frequency || prev.monthly_mode !== cur.monthly_mode}
    >
      {({ getFieldValue }) => {
        if ((getFieldValue('frequency') || 'weekly') !== 'monthly') {
          return (
            <Form.Item name="weekdays" label="Hangi Günler" rules={[{ required: true, message: 'En az bir gün seçin.' }]}>
              <Checkbox.Group options={ROUTINE_WEEKDAY_OPTIONS} />
            </Form.Item>
          );
        }

        const mode = getFieldValue('monthly_mode') || 'day_of_month';

        return (
          <>
            <Form.Item name="monthly_mode" label="Aylık Kural">
              <Select options={ROUTINE_MONTHLY_MODE_OPTIONS} />
            </Form.Item>
            {mode === 'day_of_month' ? (
              <Form.Item
                name="month_days"
                label="Ayın Günleri"
                rules={[{ required: true, message: 'En az bir gün seçin.' }]}
                extra="29, 30 veya 31 seçerseniz o günü olmayan aylarda ayın son gününde çalışır."
              >
                <Select mode="multiple" options={MONTH_DAY_OPTIONS} placeholder="Örn. 1, 15" />
              </Form.Item>
            ) : (
              <div className="flex gap-3">
                <Form.Item name="month_week" label="Kaçıncı" rules={[{ required: true, message: 'Seçin.' }]} style={{ flex: 1 }}>
                  <Select options={ROUTINE_MONTH_WEEK_OPTIONS} placeholder="1." />
                </Form.Item>
                <Form.Item name="month_weekday" label="Gün" rules={[{ required: true, message: 'Seçin.' }]} style={{ flex: 2 }}>
                  <Select options={ROUTINE_WEEKDAY_OPTIONS} placeholder="Pazartesi" />
                </Form.Item>
              </div>
            )}
          </>
        );
      }}
    </Form.Item>
    <Form.Item name="skip_weekday_if_holiday" label="Seçili gün resmi tatile denk gelirse atla" valuePropName="checked">
      <Switch />
    </Form.Item>
    <Form.Item name="also_fire_on_any_holiday" label="Tekrar kuralından bağımsız, her resmi tatilde de çalıştır" valuePropName="checked">
      <Switch />
    </Form.Item>
    <Form.Item name="require_completion_note" label="Tamamlarken not girmek zorunlu olsun" valuePropName="checked">
      <Switch />
    </Form.Item>
    <Form.Item name="notification_offsets" label="Bildirim Kuralları (dakika, negatif=önce/0=tam zamanında/pozitif=sonra)">
      <Select mode="tags" placeholder="Örn. -10, 0, 30" tokenSeparators={[',']} />
    </Form.Item>
    {isBranch && (
      <Form.Item
        name="assigned_user_ids"
        label="Atanan Kullanıcılar"
        extra={!branchId ? 'Önce şube seçin — atanabilecek kişiler şubeye göre listeleniyor.' : undefined}
      >
        <UserSearchSelect
          mode="multiple"
          allowClear
          disabled={!branchId}
          placeholder={branchId ? 'Kullanıcı ara (ad veya soyad)...' : 'Önce şube seçin'}
          fetchFn={getRoutineAssignableUsers}
          extraParams={{ branch_id: branchId }}
          initialOptions={initialAssignedUsers}
          renderLabel={(u) => `${u.name} ${u.surname}`}
        />
      </Form.Item>
    )}
  </>
);

const parseOffsets = (values) => (values || []).map((v) => parseInt(v, 10)).filter((v) => Number.isFinite(v));

const buildFormValues = (routine) => ({
  name: routine.name,
  description: routine.description,
  scheduled_time: dayjs(routine.scheduled_time, 'HH:mm:ss'),
  color: routine.color,
  frequency: routine.frequency || 'weekly',
  weekdays: routine.weekdays,
  monthly_mode: routine.monthly_mode || 'day_of_month',
  month_days: routine.month_days,
  month_week: routine.month_week,
  month_weekday: routine.month_weekday,
  skip_weekday_if_holiday: routine.skip_weekday_if_holiday,
  also_fire_on_any_holiday: routine.also_fire_on_any_holiday,
  require_completion_note: routine.require_completion_note,
  notification_offsets: (routine.notification_rules || []).map((r) => String(r.offset_minutes)),
});

/**
 * Gonderilecek govdeyi hazirlar ve KULLANILMAYAN tekrar alanlarini ATAR.
 *
 * Gerekli, cunku form her iki modun degerlerini de bellekte tutuyor: aylik
 * secildiginde weekdays eski haliyle duruyor ve gonderilirse backend'in
 * "haftalik ise weekdays zorunlu ve min:1" kurali devreye girip 422 veriyor.
 * Ters yonde de aylik alanlar bosuna gidiyordu.
 */
const buildSchedulePayload = (values) => {
  const { weekdays, monthly_mode, month_days, month_week, month_weekday, ...rest } = values;
  const frequency = values.frequency || 'weekly';

  const payload = {
    ...rest,
    frequency,
    scheduled_time: values.scheduled_time.format('HH:mm'),
    notification_offsets: parseOffsets(values.notification_offsets),
  };

  if (frequency !== 'monthly') {
    return { ...payload, weekdays };
  }

  return monthly_mode === 'nth_weekday'
    ? { ...payload, monthly_mode, month_week, month_weekday }
    : { ...payload, monthly_mode: 'day_of_month', month_days };
};

// ------------------------------------------------------------------
// Sube Rutinleri - sadece branch_manager+ yonetebilir
// ------------------------------------------------------------------
const BranchRoutinesTab = () => {
  const { user } = useAuthStore();
  const isSuperRole = TOP_ROLES.includes(user?.role);
  const [branches, setBranches] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  // Sayfalama + siralama tek yerden (bkz. hooks/useServerTable).
  const table = useServerTable('routines-branch', 15);
  const selectedBranchId = Form.useWatch('branch_id', form);

  useEffect(() => {
    if (isSuperRole) {
      getBranches({ per_page: 100 }).then((res) => setBranches(res.data.data || [])).catch(() => {});
    }
  }, [isSuperRole]);

  const fetchItems = useCallback(async (o = {}) => {
    setLoading(true);
    try {
      const res = await getRoutines({ type: 'branch', ...table.params(o) });
      setItems(res.data.data);
      setTotal(res.data.total ?? 0);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Rutinler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.page, table.pageSize, table.sorter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      color: 'green',
      frequency: 'weekly',
      monthly_mode: 'day_of_month',
      skip_weekday_if_holiday: true,
      also_fire_on_any_holiday: false,
      require_completion_note: false,
      branch_id: isSuperRole ? undefined : user?.branch_id,
    });
    setModalOpen(true);
  };

  const openEdit = (routine) => {
    setEditing(routine);
    form.setFieldsValue({
      ...buildFormValues(routine),
      branch_id: routine.branch_id,
      assigned_user_ids: (routine.assignments || []).map((a) => a.user_id),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // form validasyon hatasi - alanlarin altinda zaten gosteriliyor
    }

    setSubmitting(true);
    try {
      const payload = buildSchedulePayload(values);
      if (editing) {
        await updateRoutine(editing.id, payload);
        message.success('Rutin güncellendi.');
      } else {
        await createRoutine({ ...payload, type: 'branch' });
        message.success('Rutin oluşturuldu.');
      }
      setModalOpen(false);
      fetchItems();
    } catch (error) {
      message.error(apiErrorMessage(error, editing ? 'Güncellenemedi.' : 'Oluşturulamadı.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (routine) => {
    try {
      await toggleRoutineActive(routine.id);
      fetchItems();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Durum değiştirilemedi.'));
    }
  };

  const handleDelete = async (routine) => {
    try {
      await deleteRoutine(routine.id);
      message.success('Rutin silindi.');
      fetchItems();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Silinemedi.'));
    }
  };

  // Siralama SUNUCUDA (bkz. useServerTable). Kolon adlari backend'deki beyaz
  // listeyle birebir ayni olmali (RoutineController::applySort).
  const columns = [
    { title: 'Ad', dataIndex: 'name', ...table.sortProps('name') },
    ...(isSuperRole ? [{ title: 'Şube', dataIndex: ['branch', 'name'], ...table.sortProps('branch') }] : []),
    {
      title: 'Saat',
      dataIndex: 'scheduled_time',
      ...table.sortProps('scheduled_time'),
      render: (v) => v?.slice(0, 5),
    },
    // Aylik rutinde weekdays bos gelir; kolon tekrar kuralinin tamamini
    // ozetliyor (bkz. scheduleSummary).
    { title: 'Tekrar', key: 'schedule', render: (_, record) => scheduleSummary(record) },
    {
      title: 'Renk',
      dataIndex: 'color',
      ...table.sortProps('color'),
      render: (v) => <span style={{ color: routineColorHex(v), fontSize: 18 }}>●</span>,
    },
    { title: 'Kurallar', width: 110, align: 'center', render: (_, record) => <RulesCell routine={record} /> },
    { title: 'Atananlar', dataIndex: 'assignments', width: 240, render: (v) => <AssigneesCell assignments={v} /> },
    {
      title: 'Aktif',
      dataIndex: 'is_active',
      width: 80,
      ...table.sortProps('is_active'),
      render: (v, record) => <Switch checked={!!v} onChange={() => handleToggleActive(record)} />,
    },
    {
      title: 'İşlemler',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Düzenle">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          {isSuperRole ? (
            <Popconfirm
              title="Bu rutini silmek istediğinize emin misiniz?"
              description="Geçmiş icra kayıtları da kaybolur. Bunun yerine pasife almayı düşünün."
              onConfirm={() => handleDelete(record)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ) : (
            <Tooltip title="Şube rutinini silme yetkisi yalnızca süper adminlerdedir. Kullanımdan kaldırmak için pasife alın.">
              <Button size="small" disabled icon={<LockOutlined />} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Şube Rutinleri"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Rutin</Button>}
    >
      {!isSuperRole && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Şube rutinleri silinemez, pasife alınır."
          description="Veri bütünlüğü için geçmiş icra kayıtları korunur — kullanılmayan bir rutini “Aktif” kolonundan kapatmanız yeterlidir."
        />
      )}
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        scroll={{ x: 'max-content' }}
        {...table.tableProps(total)}
      />

      <Modal
        title={editing ? 'Rutini Düzenle' : 'Yeni Şube Rutini'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editing ? 'Güncelle' : 'Oluştur'}
        cancelText="Vazgeç"
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          {!editing && (
            <Form.Item name="branch_id" label="Şube" rules={[{ required: true, message: 'Şube seçin.' }]}>
              <BranchSelect branches={branches} />
            </Form.Item>
          )}
          <RoutineFormFields
            isBranch
            branchId={editing ? editing.branch_id : selectedBranchId}
            initialAssignedUsers={editing ? (editing.assignments || []).map((a) => a.user).filter(Boolean) : undefined}
          />
        </Form>
      </Modal>
    </Card>
  );
};

// ------------------------------------------------------------------
// Kisisel Rutinlerim - herkes KENDI ekledigini yonetir. Yoneticinin
// bir kullanici adina olusturdugu rutini o kullanici duzenleyemez/silemez
// (backend de ayni kurali uyguluyor).
// ------------------------------------------------------------------
const PersonalRoutinesTab = () => {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();
  // Sube rutinleriyle ayni yapi: sunucu sayfalama + sunucu siralama.
  const table = useServerTable('routines-personal', 15);

  const canManage = (routine) => {
    const createdById = creatorId(routine);
    // Kisinin kendi olusturdugu rutin: sahibi yonetir.
    if (createdById === routine.owner_user_id) {
      return routine.owner_user_id === user?.id;
    }
    // Yonetici baskasi adina olusturmus: sadece olusturan yonetir.
    return createdById === user?.id;
  };

  const fetchItems = useCallback(async (o = {}) => {
    setLoading(true);
    try {
      const res = await getRoutines({ type: 'personal', ...table.params(o) });
      setItems(res.data.data);
      setTotal(res.data.total ?? 0);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Rutinler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.page, table.pageSize, table.sorter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      color: 'green',
      frequency: 'weekly',
      monthly_mode: 'day_of_month',
      skip_weekday_if_holiday: true,
      also_fire_on_any_holiday: false,
      require_completion_note: false,
    });
    setModalOpen(true);
  };

  const openEdit = (routine) => {
    setEditing(routine);
    form.setFieldsValue(buildFormValues(routine));
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildSchedulePayload(values);
      if (editing) {
        await updateRoutine(editing.id, payload);
        message.success('Rutin güncellendi.');
      } else {
        await createRoutine({ ...payload, type: 'personal' });
        message.success('Rutin oluşturuldu.');
      }
      setModalOpen(false);
      fetchItems();
    } catch (error) {
      message.error(apiErrorMessage(error, editing ? 'Güncellenemedi.' : 'Oluşturulamadı.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (routine) => {
    try {
      await toggleRoutineActive(routine.id);
      fetchItems();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Durum değiştirilemedi.'));
    }
  };

  const handleDelete = async (routine) => {
    try {
      await deleteRoutine(routine.id);
      message.success('Rutin silindi.');
      fetchItems();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Silinemedi.'));
    }
  };

  // Siralama SUNUCUDA (bkz. useServerTable) - liste sayfali oldugu icin
  // istemci siralamasi yalnizca gorunen sayfayi sirar ve yaniltir.
  const columns = [
    {
      title: 'Ad',
      dataIndex: 'name',
      ...table.sortProps('name'),
      render: (v, record) => (
        <span>
          {v}
          {creatorId(record) !== record.owner_user_id && record.owner_user_id === user?.id && (
            <Tag color="gold" style={{ marginLeft: 8 }}>
              {creatorName(record) ? `${creatorName(record)} atadı` : 'Yönetici tarafından atandı'}
            </Tag>
          )}
          {record.owner_user_id !== user?.id && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {record.owner ? `${record.owner.name} ${record.owner.surname}` : 'Başka kullanıcı'}
            </Tag>
          )}
        </span>
      ),
    },
    { title: 'Saat', dataIndex: 'scheduled_time', ...table.sortProps('scheduled_time'), render: (v) => v?.slice(0, 5) },
    // Aylik rutinde weekdays bos gelir; kolon tekrar kuralinin tamamini
    // ozetliyor (bkz. scheduleSummary).
    { title: 'Tekrar', key: 'schedule', render: (_, record) => scheduleSummary(record) },
    { title: 'Renk', dataIndex: 'color', ...table.sortProps('color'), render: (v) => <span style={{ color: routineColorHex(v), fontSize: 18 }}>●</span> },
    { title: 'Kurallar', width: 110, align: 'center', render: (_, record) => <RulesCell routine={record} /> },
    {
      title: 'Aktif',
      dataIndex: 'is_active',
      width: 80,
      ...table.sortProps('is_active'),
      render: (v, record) => (
        <Switch checked={!!v} disabled={!canManage(record)} onChange={() => handleToggleActive(record)} />
      ),
    },
    {
      title: 'İşlemler',
      width: 110,
      fixed: 'right',
      render: (_, record) =>
        canManage(record) ? (
          <Space>
            <Tooltip title="Düzenle">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
            <Popconfirm title="Bu rutini silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record)} okText="Evet" cancelText="Hayır">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : (
          <Tooltip title="Bu rutin sizin adınıza yöneticiniz tarafından oluşturuldu — düzenleyemez veya silemezsiniz.">
            <Button size="small" disabled icon={<LockOutlined />} />
          </Tooltip>
        ),
    },
  ];

  return (
    <Card
      title="Kişisel Rutinlerim"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Rutin</Button>}
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        scroll={{ x: 'max-content' }}
        {...table.tableProps(total)}
      />

      <Modal
        title={editing ? 'Rutini Düzenle' : 'Yeni Kişisel Rutin'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editing ? 'Güncelle' : 'Oluştur'}
        cancelText="Vazgeç"
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <RoutineFormFields isBranch={false} />
        </Form>
      </Modal>
    </Card>
  );
};

const Routines = () => {
  const { user } = useAuthStore();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [tab, setTab] = useState('today');

  const tabOptions = [
    { label: 'Bugün', value: 'today', icon: <ClockCircleOutlined /> },
    ...(isManager ? [{ label: 'Şube Rutinleri', value: 'branch' }] : []),
    { label: 'Kişisel Rutinlerim', value: 'personal' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Rutin</h2>
        <Segmented value={tab} onChange={setTab} options={tabOptions} />
      </div>

      {tab === 'today' && <TodayTab />}
      {tab === 'branch' && isManager && <BranchRoutinesTab />}
      {tab === 'personal' && <PersonalRoutinesTab />}
    </div>
  );
};

export default Routines;
